import FeatureGuardLogic from "App/FeatureGuardLogic";
import LayoutDashboard from "components/LayoutDashboard";
import NotFound from "components/NotFound";
import ACCOUNT_FEATURE from "enums/ACCOUNT_FEATURE";
import { APP_ROUTES } from "enums/APP_ROUTES";
import AppLayout from "features/AppLayout";

import TransitionalRegion from "components/TransitionalRegion";
import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route } from "react-router-dom";

const ProjectManagement = lazy(() => import("features/ProjectManagement"));
const AccountDashboard = lazy(
  () => import("features/AccountManagement/components/AccountDashboard"),
);
const AccountSettings = lazy(
  () => import("features/AccountManagement/components/AccountSettings"),
);
const AdminListing = lazy(() => import("features/AdminManagement"));
const ViewAdminAdd = lazy(() => import("features/AdminManagement/ViewAdd"));

const ScriptListing = lazy(() => import("features/ScriptManagement"));
const ScriptDetails = lazy(
  () => import("features/ScriptManagement/ScriptDetails"),
);
const ScriptAdd = lazy(() => import("features/ScriptManagement/ViewAdd"));

const ParticipantListing = lazy(() => import("features/ParticipantManagement"));
const ParticipantImports = lazy(
  () => import("features/ParticipantManagement/ParticipantImports"),
);
const ParticipantGift = lazy(
  () => import("features/ParticipantManagement/ViewGift"),
);
const ParticipantInvite = lazy(
  () => import("features/ParticipantManagement/ViewInvite"),
);
const ParticipantProfile = lazy(
  () => import("features/ParticipantManagement/ViewProfile"),
);

const PlaylistListing = lazy(
  () => import("features/PlaylistManagement/PlaylistListing"),
);
const PlaylistDetails = lazy(
  () => import("features/PlaylistManagement/PlaylistDetails"),
);
const PlaylistAdd = lazy(() => import("features/PlaylistManagement/ViewAdd"));

const DocumentManagement = lazy(() => import("features/DocumentManagement"));
const SyntheticPersonas = lazy(() => import("features/SyntheticPersonas"));

const VideoListing = lazy(
  () => import("features/VideoManagement/VideoListing"),
);
const VideoReview = lazy(() => import("features/VideoManagement/VideoReview"));
const ViewUpload = lazy(() => import("features/VideoManagement/VideoUpload"));

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Suspense fallback={<TransitionalRegion noResultsText={<></>} />}>
    {children}
  </Suspense>
);

const AuthenticatedRoutes = (
  <>
    <Route
      element={
        <SuspenseWrapper>
          <LayoutDashboard />
        </SuspenseWrapper>
      }
    >
      <Route path="dashboard" element={<ProjectManagement />} />
      <Route path="dashboard/:activeId" element={<ProjectManagement />} />
      <Route path="account/:accountId" element={<AccountDashboard />} />
      <Route path="account/:accountId/settings" element={<AccountSettings />} />
    </Route>

    <Route
      path="project"
      element={
        <SuspenseWrapper>
          <Outlet />
        </SuspenseWrapper>
      }
    >
      <Route index element={<Navigate to="/dashboard" />} />

      <Route path=":slug" element={<AppLayout />}>
        <Route index element={<Navigate to={APP_ROUTES.SCRIPTS} />} />

        <Route path={APP_ROUTES.ADMINS} element={<AdminListing />} />
        <Route path={`${APP_ROUTES.ADMINS}/add`} element={<ViewAdminAdd />} />

        <Route path={APP_ROUTES.SCRIPTS} element={<ScriptListing />}>
          <Route path=":activeId" element={<ScriptDetails />} />
        </Route>
        <Route path={`${APP_ROUTES.SCRIPTS}/add`} element={<ScriptAdd />} />

        <Route
          path={`${APP_ROUTES.PARTICIPANTS}/add`}
          element={<ParticipantImports />}
        />
        <Route path={APP_ROUTES.PARTICIPANTS} element={<ParticipantListing />}>
          <Route path="invite" element={<ParticipantInvite />} />
          <Route path="gift" element={<ParticipantGift />} />
          <Route path="profile" element={<ParticipantProfile />} />
        </Route>

        <Route path={`${APP_ROUTES.VIDEOS}/upload`} element={<ViewUpload />} />
        <Route
          path={`${APP_ROUTES.VIDEOS}/:listingType`}
          element={<VideoListing />}
        />
        <Route
          path={`${APP_ROUTES.VIDEOS}/:listingType/review`}
          element={<VideoReview />}
        />
        <Route
          path={`${APP_ROUTES.VIDEOS}/:listingType/review/:activeId`}
          element={<VideoReview />}
        />

        <Route path={APP_ROUTES.PLAYLISTS} element={<PlaylistListing />} />
        <Route path={`${APP_ROUTES.PLAYLISTS}/add`} element={<PlaylistAdd />} />
        <Route
          path={`${APP_ROUTES.PLAYLISTS}/:activeId`}
          element={<PlaylistDetails />}
        />

        <Route path={APP_ROUTES.DOCUMENTS} element={<DocumentManagement />} />

        <Route
          element={<FeatureGuardLogic featureName={ACCOUNT_FEATURE.PERSONAS} />}
        >
          <Route path={APP_ROUTES.PERSONAS} element={<SyntheticPersonas />} />
        </Route>

        <Route path={APP_ROUTES.HYDRA} element={<NotFound />} />
      </Route>
    </Route>
  </>
);

export default AuthenticatedRoutes;
